import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TablePagination,
  Button,
  IconButton,
  Box,
  TextField,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CancelIcon from "@mui/icons-material/Cancel";
import { useNavigate } from "react-router-dom";
import defaultImg from "../../assets/default-image.png";
import { productApi } from "../../api";
import DOMPurify from "dompurify";

import RichTextEditor from "../../components/RichTextEditor";

const Products = () => {
  const navigate = useNavigate();
  const newProductRowRef = useRef(null);
  const [productList, setProductList] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [showEditForm, setShowEditForm] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [openPriceManageModal, setOpenPriceManageModal] = useState(false);
  const [priceManageData, setPriceManageData] = useState({
    prices: {},
    isNew: false,
  });

  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [editImageModalOpen, setEditImageModalOpen] = useState(false);
  const [currentEditImages, setCurrentEditImages] = useState([]);
  const fileInputRef = React.useRef(null);

  // Add pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleEditImageClick = () => {
    setCurrentEditImages(editValues.images || []);
    setEditImageModalOpen(true);
  };

  const handleEditImageUpload = (e) => {
    if (!e?.target?.files) return;

    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    console.log("Uploaded files:", files);

    const newImages = files.map((file) => ({
      url: URL.createObjectURL(file),
      file,
    }));

    console.log("New images with previews:", newImages);

    // Update both states with the new images
    const updatedImages = [...(editValues.images || []), ...newImages];
    setEditValues((prev) => ({
      ...prev,
      images: updatedImages,
    }));
    setCurrentEditImages(updatedImages);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveImage = (indexToRemove) => {
    if (typeof indexToRemove !== "number" || indexToRemove < 0) return;

    // Keep all images except the one being removed
    const updatedImages = currentEditImages.filter(
      (_, idx) => idx !== indexToRemove
    );

    // Update both states with the remaining images
    setCurrentEditImages(updatedImages);
    setEditValues((prev) => ({
      ...prev,
      images: updatedImages,
    }));

    // If we removed the current image, update the current index
    if (currentImageIndex >= updatedImages.length) {
      setCurrentImageIndex(Math.max(0, updatedImages.length - 1));
    }
  };

  useEffect(() => {
    console.log("Updated images:", editValues.images);
  }, [editValues.images]);

  const handleImageClick = (images) => {
    if (!images || !Array.isArray(images) || images.length === 0) return;
    setSelectedImages(images);
    setCurrentImageIndex(0);
    setImageModalOpen(true);
  };

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const products = await productApi.getAllProducts();
      const sortedProducts = products.sort((a, b) => a.id - b.id);
      setProductList(sortedProducts);
    } catch (error) {
      showSnackbar("Failed to fetch products", "error");
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleEditClick = useCallback((product) => {
    setEditId(product.id);

    // Format the product data for editing
    const formattedProduct = {
      ...product,
      // Keep the original images array
      images: product.images || [],
      // Format prices as an array
      prices: product.prices
        ? product.prices.map((price) => ({
            name: price.name,
            value: price.value,
            stock: price.stock,
          }))
        : [],
      variation: product.variation || product.prices?.[0]?.name || "",
    };

    console.log("📝 Formatted product data for editing:", formattedProduct);
    setEditValues(formattedProduct);
    setShowEditForm(true);
  }, []);

  const handlePriceManageSave = useCallback((newPrices) => {
    if (!newPrices || typeof newPrices !== "object") return;

    // Convert the prices object to the format expected by the backend
    const formattedPrices = Object.entries(newPrices)
      .filter(([_, data]) => data?.size && data.size.trim() !== "") // Only include variations with a name
      .map(([_, data]) => ({
        name: data?.size || "", // Use the variation name
        value: String(data?.price || 0), // Convert price to string
        stock: Number(data?.stock || 0),
      }));

    setEditValues((prev) => ({
      ...prev,
      prices: formattedPrices,
    }));
    setOpenPriceManageModal(false);
  }, []);

  const handleManagePrices = useCallback(
    (productId) => {
      // Get the current product data
      const currentProduct = productId
        ? productList.find((p) => p.id === productId)
        : editValues;

      // Convert prices array from DB to object format for the modal
      const pricesObject = Array.isArray(currentProduct.prices)
        ? currentProduct.prices.reduce(
            (acc, price) => ({
              ...acc,
              [price.name]: {
                price: price.value || "",
                stock: price.stock || "",
                size: price.name,
                displaySize: price.name,
              },
            }),
            {}
          )
        : currentProduct.prices || {};

      setPriceManageData({
        prices: pricesObject,
        isNew: !productId,
      });
      setOpenPriceManageModal(true);
    },
    [productList, editValues]
  );

  useEffect(() => {
    const checkReturnFromPriceManage = () => {
      // Check for new product first
      const newProductData = localStorage.getItem("product_new");
      if (newProductData) {
        const data = JSON.parse(newProductData);
        if (data.isEditing) {
          // Set the new product form to be visible and restore prices
          setEditValues({
            ...data,
            prices: data.prices || {},
          });
          setShowEditForm(true);
          // Remove the flag but keep the prices
          localStorage.setItem(
            "product_new",
            JSON.stringify({
              ...data,
              isEditing: false,
            })
          );
          return;
        }
      }

      // Then check for existing product
      const existingProductData = localStorage.getItem("product_edit");
      if (existingProductData) {
        const data = JSON.parse(existingProductData);
        if (data.isEditing) {
          setEditId(data.id);
          setEditValues({
            ...data,
            prices: data.prices || {},
          });
          setShowEditForm(true);
          // Remove the flag but keep the prices
          localStorage.setItem(
            "product_edit",
            JSON.stringify({
              ...data,
              isEditing: false,
            })
          );
        }
      }
    };

    checkReturnFromPriceManage();
  }, []);

  const handleAddClick = useCallback(() => {
    const newProduct = {
      images: [],
      title: "",
      description: "",
      prices: [],
      status: true,
    };
    setEditId(null);
    setEditValues(newProduct);
    setShowEditForm(true);

    // Add a small delay to ensure the new row is rendered
    setTimeout(() => {
      if (newProductRowRef.current) {
        newProductRowRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 100);
  }, []);

  const handleSaveClick = useCallback(async () => {
    try {
      setSaving(true); // Start loading
      // Create a copy of the current edit values with the updated images
      let finalEditValues = {
        ...editValues,
        images: currentEditImages, // Use the current images state which has the correct remaining images
      };

      console.log("current images:", currentEditImages);
      console.log("editvalues:", finalEditValues.images);

      const savedData = localStorage.getItem(`product_${editId || "new"}`);
      if (savedData) {
        const data = JSON.parse(savedData);
        if (data.prices && Object.keys(data.prices).length > 0) {
          finalEditValues.prices = data.prices;
        }
        localStorage.removeItem(`product_${editId || "new"}`);
      }

      // Filter out any blob URLs or data URLs from the images array
      finalEditValues.images = finalEditValues.images.filter((img) => {
        if (typeof img === "string") {
          return !img.startsWith("blob:") && !img.startsWith("data:");
        }
        return true; // Keep File objects and other valid image objects
      });

      // Add a delay to simulate network latency
      await new Promise((resolve) => setTimeout(resolve, 2000));

      if (editId === null) {
        await productApi.createProduct(finalEditValues);
        // Scroll to top after adding new product
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        await productApi.updateProduct(editId, finalEditValues);
      }
      showSnackbar("Product saved successfully", "success");
      setEditId(null);
      setEditValues({});
      setCurrentEditImages([]);
      setShowEditForm(false);
      fetchProducts();
    } catch (error) {
      // Extract just the error message from the error object
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Failed to save product";
      showSnackbar(errorMessage, "error");
    } finally {
      setSaving(false); // End loading
    }
  }, [editId, editValues, currentEditImages, fetchProducts, showSnackbar]);

  const handleChange = useCallback((field, value) => {
    if (!field) return;

    if (field === "description") {
      // Save HTML content for the description
      setEditValues((prev) => ({
        ...prev,
        description: value || "", // Ensure we always have a string value
      }));
    } else if (field === "variation") {
      setEditValues((prev) => ({
        ...prev,
        variation: value || "",
        prices: {
          ...(prev.prices || {}),
          [value || ""]: {
            price: prev.prices?.[value]?.price || 0,
            stock: prev.prices?.[value]?.stock || 0,
          },
        },
      }));
    } else if (field.startsWith("price-")) {
      const variationKey = field.split("-")[1];
      if (!variationKey) return;

      setEditValues((prev) => ({
        ...prev,
        prices: {
          ...(prev.prices || {}),
          [variationKey]: {
            price: parseFloat(value) || 0,
            stock: prev.prices?.[variationKey]?.stock || 0,
          },
        },
      }));
    } else {
      setEditValues((prev) => ({ ...prev, [field]: value }));
    }
  }, []);

  const handleDeleteClick = useCallback((id) => {
    setProductToDelete(id);
    setOpenDeleteDialog(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!productToDelete) return;

    try {
      await productApi.deleteProduct(productToDelete);
      showSnackbar("Product deleted successfully");
      fetchProducts();
      if (editId === productToDelete) {
        setEditId(null);
        setEditValues({});
        setShowEditForm(false);
      }
    } catch (err) {
      showSnackbar("Failed to delete product", "error");
    } finally {
      setOpenDeleteDialog(false);
      setProductToDelete(null);
    }
  }, [productToDelete, editId, fetchProducts, showSnackbar]);

  const handleCancelDelete = useCallback(() => {
    setOpenDeleteDialog(false);
    setProductToDelete(null);
  }, []);

  const handleVariationChange = useCallback(
    (productId, newVariation) => {
      if (!productId) return;

      if (editId === productId) {
        setEditValues((prev) => ({
          ...prev,
          variation: newVariation || "small",
        }));
      } else {
        setProductList((prev) =>
          prev.map((p) =>
            p.id === productId
              ? { ...p, variation: newVariation || "small" }
              : p
          )
        );
      }
    },
    [editId]
  );

  const getImageUrl = useCallback((images) => {
    if (!images || images.length === 0) return defaultImg;

    const image = images[0];
    if (!image) return defaultImg;

    // If image is a string (base64 or remote URL)
    if (typeof image === "string") {
      if (image.startsWith("data:") || image.startsWith("http")) return image;
      return `${process.env.REACT_APP_API_URL}${image.replace(/\/+/g, "/")}`;
    }

    // If image is a File object (e.g., user-selected)
    if (image instanceof File) {
      return URL.createObjectURL(image);
    }

    // If image is an object with a `url` field (preview URL)
    if (typeof image === "object" && image?.url) {
      // If it's a blob URL or data URL, return it directly
      if (image.url.startsWith("blob:") || image.url.startsWith("data:")) {
        return image.url;
      }
      // If it's a backend URL, add the API URL prefix
      if (image.url.startsWith("http")) {
        return image.url;
      }
      return `${process.env.REACT_APP_API_URL}${image.url.replace(
        /\/+/g,
        "/"
      )}`;
    }

    // If image is an object with a `file` field (new upload)
    if (typeof image === "object" && image?.file) {
      return URL.createObjectURL(image.file);
    }

    return defaultImg;
  }, []);

  const handlePreviewClick = useCallback(() => {
    navigate("/products-preview");
  }, [navigate]);

  const handleCancelClick = useCallback(() => {
    setEditId(null);
    setEditValues({});
    setShowEditForm(false);
  }, []);

  const handleStatusChange = useCallback(
    async (productId, newStatus) => {
      try {
        const product = productList.find((p) => p.id === productId);
        if (!product) return;

        const updatedProduct = {
          ...product,
          status: newStatus,
        };

        await productApi.updateProduct(productId, updatedProduct);
        showSnackbar(
          `Product ${newStatus ? "activated" : "deactivated"} successfully`
        );
        fetchProducts();
      } catch (error) {
        showSnackbar("Failed to update product status", "error");
      }
    },
    [productList, fetchProducts, showSnackbar]
  );

  // Add pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calculate paginated products
  const paginatedProducts = productList.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: "100%", p: 3 }}>
      <Typography variant="h5" align="center" gutterBottom>
        My Products
      </Typography>
      <Box display="flex" justifyContent="flex-end" gap={2} mb={2}>
        <Button
          variant="outlined"
          startIcon={<VisibilityIcon />}
          onClick={handlePreviewClick}
        >
          Preview
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
        >
          Add
        </Button>
      </Box>

      {/* Add top pagination */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="body2" sx={{ mr: 1 }}>
            Rows per page:
          </Typography>
          <Select
            value={rowsPerPage}
            onChange={handleChangeRowsPerPage}
            size="small"
            sx={{ minWidth: 80 }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </Box>
        <TablePagination
          component="div"
          count={productList.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[]} // Empty array to hide the rows per page dropdown
        />
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
        <Table
          sx={{
            border: "1px solid rgba(224, 224, 224, 1)",
            "& .MuiTableCell-root": {
              border: "1px solid rgba(224, 224, 224, 1)",
            },
            "& .MuiTableHead-root .MuiTableCell-root": {
              borderTop: "none",
              borderLeft: "none",
              borderRight: "none",
              borderBottom: "1px solid rgba(224, 224, 224, 1)",
            },
            "& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root":
              {
                borderBottom: "none",
              },
            "& .MuiTableBody-root .MuiTableCell-root:first-of-type": {
              borderLeft: "none",
            },
            "& .MuiTableBody-root .MuiTableCell-root:last-of-type": {
              borderRight: "none",
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell align="center">
                <Typography>No</Typography>
              </TableCell>
              <TableCell align="center">Image</TableCell>
              <TableCell align="center">Title</TableCell>
              <TableCell align="center">Description</TableCell>
              <TableCell align="center">Variations & Price</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedProducts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                  <Typography variant="h6" color="textSecondary">
                    No data available
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedProducts.map((product, index) => (
                <TableRow key={product.id}>
                  <TableCell align="center">{index + 1}</TableCell>
                  <TableCell align="center">
                    {editId === product.id ? (
                      <Box
                        component="img"
                        src={getImageUrl(editValues.images)}
                        alt={editValues.title || "Product"}
                        onClick={handleEditImageClick}
                        sx={{
                          width: 64,
                          height: 64,
                          objectFit: "cover",
                          borderRadius: 1,
                          margin: "0 auto",
                          cursor: "pointer",
                          border: "2px dashed #ccc",
                          opacity: 0.8,
                        }}
                      />
                    ) : (
                      <Box
                        component="img"
                        src={getImageUrl(product.images)}
                        onClick={() => handleImageClick(product.images)}
                        sx={{
                          width: 64,
                          height: 64,
                          objectFit: "cover",
                          borderRadius: 1,
                          margin: "0 auto",
                          cursor: "pointer",
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {editId === product.id ? (
                      <TextField
                        value={editValues.title}
                        onChange={(e) => handleChange("title", e.target.value)}
                        variant="standard"
                        sx={{ textAlign: "center" }}
                      />
                    ) : (
                      product.title
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {editId === product.id ? (
                      <RichTextEditor
                        value={editValues.description || ""}
                        onChange={(html) => {
                          console.log("Description changed:", html);
                          handleChange("description", html);
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          textAlign: "left",
                          backgroundColor: "#f9f9f9",
                          padding: "8px",
                          borderRadius: 2,
                          border: "1px solid #ddd",
                          minWidth: 300,
                          fontSize: "0.95rem",
                          maxHeight: 200,
                          overflowY: "auto",
                          "& p": {
                            margin: "0 0 8px 0",
                          },
                          "& ul, & ol": {
                            margin: "0 0 8px 0",
                            paddingLeft: "20px",
                          },
                          "& li": {
                            margin: "4px 0",
                          },
                          "& h1, & h2, & h3, & h4, & h5, & h6": {
                            margin: "12px 0 8px 0",
                          },
                          "& img": {
                            maxWidth: "100%",
                            height: "auto",
                          },
                        }}
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(product.description || ""),
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {editId === product.id ? (
                      <Box
                        display="flex"
                        gap={1}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleManagePrices(product.id)}
                        >
                          Manage
                        </Button>
                      </Box>
                    ) : (
                      <Box
                        display="flex"
                        flexDirection="column"
                        gap={1}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Box display="flex" gap={1} alignItems="center">
                          <Select
                            value={
                              product.variation ||
                              product.prices?.[0]?.name ||
                              ""
                            }
                            onChange={(e) =>
                              handleVariationChange(product.id, e.target.value)
                            }
                            variant="standard"
                            sx={{ minWidth: 100 }}
                          >
                            {product.prices &&
                              product.prices.map((price) => (
                                <MenuItem key={price.name} value={price.name}>
                                  {price.name}
                                </MenuItem>
                              ))}
                          </Select>
                        </Box>
                        <Box display="flex" gap={2}>
                          <Typography variant="body2">
                            Price: $
                            {(product.prices &&
                              product.prices.find(
                                (p) =>
                                  p.name ===
                                  (product.variation ||
                                    product.prices?.[0]?.name)
                              )?.value) ||
                              0}
                          </Typography>
                          <Typography variant="body2">
                            Stock:{" "}
                            {(product.prices &&
                              product.prices.find(
                                (p) =>
                                  p.name ===
                                  (product.variation ||
                                    product.prices?.[0]?.name)
                              )?.stock) ||
                              0}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={product.status === true}
                          onChange={(e) =>
                            handleStatusChange(product.id, e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label={product.status === true ? "Active" : "Not Active"}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box
                      sx={{ display: "flex", justifyContent: "center", gap: 1 }}
                    >
                      {editId === product.id ? (
                        <>
                          <IconButton
                            color="primary"
                            onClick={handleSaveClick}
                            disabled={saving}
                          >
                            {saving ? (
                              <CircularProgress size={24} color="primary" />
                            ) : (
                              <SaveIcon />
                            )}
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={handleCancelClick}
                            disabled={saving}
                          >
                            <CancelIcon />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton
                            color="primary"
                            onClick={() => handleEditClick(product)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteClick(product.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
            {!editId && showEditForm && Object.keys(editValues).length > 0 && (
              <TableRow ref={newProductRowRef}>
                <TableCell>New</TableCell>
                <TableCell>
                  <Box
                    component="img"
                    src={getImageUrl(editValues.images)}
                    alt={editValues.title || "Product"}
                    onClick={handleEditImageClick}
                    sx={{
                      width: 64,
                      height: 64,
                      objectFit: "cover",
                      borderRadius: 1,
                      margin: "0 auto",
                      cursor: "pointer",
                      border: "2px dashed #ccc",
                      opacity: 0.8,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    value={editValues.title || ""}
                    onChange={(e) => handleChange("title", e.target.value)}
                    variant="standard"
                    placeholder="Enter title"
                  />
                </TableCell>
                <TableCell>
                  <RichTextEditor
                    value={editValues.description || ""}
                    onChange={(html) => {
                      console.log("Description changed:", html);
                      handleChange("description", html);
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Box
                    display="flex"
                    gap={1}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleManagePrices(null)}
                    >
                      Manage
                    </Button>
                  </Box>
                </TableCell>
                <TableCell>
                  <IconButton
                    color="primary"
                    onClick={handleSaveClick}
                    disabled={saving}
                  >
                    {saving ? (
                      <CircularProgress size={24} color="primary" />
                    ) : (
                      <SaveIcon />
                    )}
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={handleCancelClick}
                    disabled={saving}
                  >
                    <CancelIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add bottom pagination */}
      <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
        <TablePagination
          component="div"
          count={productList.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[]} // Empty array to hide the rows per page dropdown
        />
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Dialog open={openDeleteDialog} onClose={handleCancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this product?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        maxWidth="md"
      >
        <DialogContent sx={{ textAlign: "center", padding: 3 }}>
          <Box
            component="img"
            src={getImageUrl([selectedImages[currentImageIndex]])}
            alt="Centered"
            sx={{
              width: 300,
              height: 300,
              objectFit: "cover",
              borderRadius: 2,
              mb: 5, // Increased margin bottom
              display: "block",
              margin: "0 auto",
            }}
          />
          <Box
            display="flex"
            justifyContent="center"
            gap={0.5}
            flexWrap="wrap"
            mt={3}
          >
            {selectedImages.map((img, idx) => (
              <Box
                key={idx}
                component="img"
                src={getImageUrl([img])}
                onClick={() => setCurrentImageIndex(idx)}
                sx={{
                  width: 80, // Slightly larger image thumbnails
                  height: 80,
                  objectFit: "cover",
                  borderRadius: 1,
                  border:
                    currentImageIndex === idx
                      ? "2px solid blue"
                      : "1px solid #ccc",
                  cursor: "pointer",
                  margin: 1, // Added margin between thumbnails
                  transition: "border 0.3s ease",
                  "&:hover": {
                    border: "2px solid #007BFF", // Hover effect
                  },
                }}
              />
            ))}
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={editImageModalOpen}
        onClose={() => setEditImageModalOpen(false)}
        maxWidth="md"
      >
        <DialogTitle>Product Images</DialogTitle>
        <DialogContent sx={{ textAlign: "center", padding: 3 }}>
          <Box
            component="img"
            src={getImageUrl([currentEditImages[currentImageIndex]])}
            alt="Main"
            sx={{
              width: 300,
              height: 300,
              objectFit: "cover",
              borderRadius: 2,
              margin: "0 auto 24px auto",
            }}
          />
          <Box
            display="flex"
            justifyContent="center"
            flexWrap="wrap"
            gap={1}
            mb={2}
          >
            {currentEditImages.map((img, idx) => (
              <Box
                key={idx}
                sx={{
                  position: "relative",
                  width: 80,
                  height: 80,
                  margin: 1,
                }}
              >
                <Box
                  component="img"
                  src={getImageUrl([img])}
                  alt={`Thumb-${idx}`}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: 1,
                    border: "1px solid #ccc",
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => handleRemoveImage(idx)}
                  sx={{
                    position: "absolute",
                    top: -8,
                    right: -8,
                    backgroundColor: "#fff",
                    border: "1px solid #ccc",
                    padding: 0.5,
                    zIndex: 1,
                    "&:hover": {
                      backgroundColor: "#f44336",
                      color: "#fff",
                    },
                  }}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>

          <Button
            variant="outlined"
            onClick={() => fileInputRef.current?.click()}
            sx={{ mt: 2 }}
          >
            Upload Image
          </Button>
          <input
            type="file"
            accept="image/*"
            multiple
            ref={fileInputRef}
            onChange={handleEditImageUpload}
            style={{ display: "none" }}
          />
        </DialogContent>
      </Dialog>

      {/* Price Management Modal */}
      <Dialog
        open={openPriceManageModal}
        onClose={() => setOpenPriceManageModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Manage Prices</DialogTitle>
        <DialogContent>
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Variation</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Stock</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(priceManageData.prices).map(([key, data], index) => (
                  <TableRow key={key}>
                    <TableCell>
                      <TextField
                        value={data.displaySize || ""}
                        onChange={(e) => {
                          const newPrices = { ...priceManageData.prices };
                          newPrices[key] = {
                            ...newPrices[key],
                            size: e.target.value,
                            displaySize: e.target.value,
                          };
                          setPriceManageData((prev) => ({
                            ...prev,
                            prices: newPrices,
                          }));
                        }}
                        size="small"
                        fullWidth
                        placeholder="Enter variation name"
                        required
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={data.price}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Only allow numbers and decimal point
                          if (value === "" || /^\d*\.?\d*$/.test(value)) {
                            const newPrices = { ...priceManageData.prices };
                            newPrices[key] = {
                              ...newPrices[key],
                              price: value,
                            };
                            setPriceManageData((prev) => ({
                              ...prev,
                              prices: newPrices,
                            }));
                          }
                        }}
                        size="small"
                        fullWidth
                        placeholder="Enter price"
                        required
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={data.stock}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Only allow numbers
                          if (value === "" || /^\d*$/.test(value)) {
                            const newPrices = { ...priceManageData.prices };
                            newPrices[key] = {
                              ...newPrices[key],
                              stock: value,
                            };
                            setPriceManageData((prev) => ({
                              ...prev,
                              prices: newPrices,
                            }));
                          }
                        }}
                        size="small"
                        fullWidth
                        placeholder="Enter stock"
                        required
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="error"
                        onClick={() => {
                          const newPrices = { ...priceManageData.prices };
                          delete newPrices[key];
                          setPriceManageData((prev) => ({
                            ...prev,
                            prices: newPrices,
                          }));
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => {
                const newKey = `new_${Date.now()}`;
                const newPrices = {
                  ...priceManageData.prices,
                  [newKey]: {
                    price: "",
                    stock: "",
                    size: "",
                    displaySize: "",
                  },
                };
                setPriceManageData((prev) => ({ ...prev, prices: newPrices }));
              }}
              disabled={Object.values(priceManageData.prices).some(
                (data) => !data.displaySize || !data.price || !data.stock
              )}
            >
              Add Variation
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPriceManageModal(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => handlePriceManageSave(priceManageData.prices)}
            disabled={Object.values(priceManageData.prices).some(
              (data) => !data.displaySize || !data.price || !data.stock
            )}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Products;
